import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import PropTypes from "prop-types";

import SitePage from "../components/SitePage";
import useSiteAssignablePlugins from "../hooks/useSiteAssignablePlugins";
import { siteRemove } from "shared-frontend/redux/actions/site";
import { retrieveSites } from "shared-frontend/redux/actions/sites";
import { addLicensesModalClose, addLicensesModalOpen } from "../redux/actions/subscriptions";
import { getAddSubscriptionModal } from "../redux/selectors/ui/subscriptions";
import { getSites } from "shared-frontend/redux/selectors/entities/sites";
import { getSite } from "../redux/selectors/ui/site";
import { getProvisionersById } from "shared-frontend/redux/selectors/entities/provisioners";
import { getAllProvisionerData } from "shared-frontend/redux/actions/provisionerData";

/**
 * The site page's "more info" affordance, which has never done anything.
 *
 * Kept because the presentational component declares it required; removing it is a change to that
 * component's contract, not to this container.
 *
 * @returns {void}
 */
function noop() {}

/**
 * Pushes the two data-layer events a successful activation reports.
 *
 * Both payloads are reproduced exactly as the pre-TanStack code sent them, because GTM matches on the
 * shapes rather than on anything self-describing:
 *
 * - `toggleProductSubscription` is a direct push with a top-level event name and a flat payload. Its
 *   `product_name` key carries the product *group* name, which is what it always carried — the field
 *   supplying it is now called `productGroupName`, but renaming the key would break the tag.
 * - `activate_subscription` used to come from redux-beacon, which wrapped every event in a
 *   `redux_event` envelope with the real name nested under `redux_event_params.type`. It no longer
 *   originates from a Redux action, but the envelope has to survive the move or GTM stops recognising
 *   it. Its `subscription` is the subscription's as-sold name, not the plugin's.
 *
 * @param {Object} plugin   The plugin row being toggled.
 * @param {Object} site     The site being toggled on.
 * @param {boolean} enabled Whether the plugin is being enabled.
 *
 * @returns {void}
 */
function pushToggleEvents( plugin, site, enabled ) {
	// GTM seeds window.dataLayer from index.html, but a late-loading snippet should not crash a toggle.
	window.dataLayer = window.dataLayer || [];

	/* eslint-disable camelcase -- GTM data layer keys. */
	window.dataLayer.push( {
		product_name: plugin.name,
		product_id: plugin.sourceId,
		toggle_state: enabled,
		event: "toggleProductSubscription",
	} );

	// Only activation reports this; deactivation never did.
	if ( ! enabled ) {
		return;
	}

	window.dataLayer.push( {
		event: "redux_event",
		redux_event_params: {
			type: "activate_subscription",
			data: {
				site: site.url,
				type: site.type,
				subscription: plugin.subscriptionName,
			},
		},
	} );
	/* eslint-enable camelcase */
}

/**
 * The page for one of the customer's sites, listing the plugins assignable to it.
 *
 * The plugin rows come from `Customers/:customerId/sites/:siteId/assignable-subscriptions`, which
 * replaces four unbounded fetches — every subscription, every product, every product group and the
 * provisioner data — and the client-side pipeline that reduced them into per-plugin slot counts. That
 * pipeline also mutated Redux entities in place to record which subscriptions were enabled here, which
 * under TanStack would corrupt cached query data for every other consumer of the key.
 *
 * Redux still owns three things that are genuinely client state or unrelated to this read model: the
 * site entity list, the out-of-licenses modal, and disconnecting the site.
 *
 * @param {Object} props       The component props.
 * @param {Object} props.match The router match, carrying the site id.
 *
 * @returns {React.JSX.Element} The rendered site page.
 */
function SitePageContainer( { match } ) {
	const dispatch = useDispatch();
	const siteId   = match.params.id;

	const sites            = useSelector( getSites );
	const provisionersById = useSelector( getProvisionersById );
	const sitesRetrieved   = useSelector( state => state.ui.sites.sitesRetrieved );
	const addSubscriptionModal = useSelector( getAddSubscriptionModal );
	const uiSiteState      = useSelector( getSite );

	const {
		plugins,
		isLoading: loadingSubscriptions,
		addSubscription,
		removeSubscription,
		togglingSubscriptionIds,
	} = useSiteAssignablePlugins( siteId );

	// The site list and the provisioner data are still Redux-backed, and nothing else on this route
	// fetches them.
	useEffect( () => {
		dispatch( retrieveSites() );
		dispatch( getAllProvisionerData() );
	}, [ dispatch ] );

	const storedSite = sites.byId[ siteId ];

	// Copied rather than assigned onto: the entity in the store must not gain a `provisioner` key, which
	// is what the previous implementation did.
	const site = useMemo(
		() => ( storedSite
			? { ...storedSite, provisioner: provisionersById[ storedSite.provisionerId ] }
			: undefined ),
		[ storedSite, provisionersById ],
	);

	const onToggleSubscription = useCallback( ( subscriptionId, sourceId, productName, enabled ) => {
		const plugin = plugins.find( candidate => candidate.sourceId === sourceId );
		if ( ! plugin || ! site ) {
			return;
		}

		const onSuccess = () => pushToggleEvents( plugin, site, enabled );

		if ( enabled ) {
			addSubscription.mutate( { id: siteId, data: { subscriptionId } }, { onSuccess } );
			return;
		}

		removeSubscription.mutate( { id: siteId, subscriptionId }, { onSuccess } );
	}, [ plugins, site, siteId, addSubscription, removeSubscription ] );

	const onToggleDisabled = useCallback(
		storeUrl => dispatch( addLicensesModalOpen( storeUrl ) ),
		[ dispatch ],
	);

	const onClose = useCallback( () => dispatch( addLicensesModalClose() ), [ dispatch ] );

	const onRemove = useCallback( () => {
		// eslint-disable-next-line no-alert
		if ( window.confirm( "Are you sure you want to remove this site from MyYoast?" ) ) {
			dispatch( siteRemove( siteId, () => dispatch( push( "/sites/" ) ) ) );
		}
	}, [ dispatch, siteId ] );

	// The toggle's pending and error state used to live in Redux, updated by the thunks' request and
	// failure actions. The mutations carry it themselves now.
	//
	// Keyed on the joined ids, and rebuilt from that key inside the memo: `useMutationState` returns a new
	// array every render, so closing over it would rebuild this object each time and defeat the memo.
	const togglingKey = togglingSubscriptionIds.join( "," );
	const uiSite = useMemo( () => ( {
		...uiSiteState,
		subscriptions: {
			togglingSubscriptionIds: togglingKey ? togglingKey.split( "," ) : [],
			error: addSubscription.error ?? removeSubscription.error ?? null,
		},
	} ), [ uiSiteState, togglingKey, addSubscription.error, removeSubscription.error ] );

	// The handlers are passed in every branch, including the two that return before reading them: they
	// are declared `isRequired`, so omitting them would log a propTypes warning for a site that is
	// merely still loading.
	const handlers = {
		onMoreInfoClick: noop,
		onToggleSubscription,
		onToggleDisabled,
		onClose,
		onRemove,
	};

	if ( ! storedSite ) {
		return sitesRetrieved
			? <SitePage siteNotFound={ true } { ...handlers } />
			: <SitePage loadingSite={ true } { ...handlers } />;
	}

	return (
		<SitePage
			site={ site }
			uiSite={ uiSite }
			plugins={ plugins }
			loadingSubscriptions={ loadingSubscriptions }
			addSubscriptionModal={ addSubscriptionModal }
			{ ...handlers }
		/>
	);
}

SitePageContainer.propTypes = {
	match: PropTypes.shape( {
		params: PropTypes.shape( { id: PropTypes.string.isRequired } ).isRequired,
	} ).isRequired,
};

export default SitePageContainer;
